import React from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';

/**
 * Internal link configuration for SEO pages
 */
export interface InternalLink {
  /**
   * URL path for the internal link
   */
  href: string;
  /**
   * Display label for the link
   */
  label: string;
}

/**
 * Props for InternalLinksSection component
 */
export interface InternalLinksSectionProps {
  /**
   * Title for the section (default: "Learn More About Email Ferret")
   */
  title?: string;
  /**
   * Description text below the title (optional)
   */
  description?: string;
  /**
   * Array of internal links to display
   */
  links: InternalLink[];
  /**
   * Optional custom className for the section wrapper
   */
  className?: string;
}

/**
 * Reusable internal links section component for SEO pages
 *
 * Provides consistent styling and structure for internal linking sections
 * that help with SEO and user navigation. Used across all SEO money pages
 * to link to related content.
 *
 * @param title - Section heading (default: "Learn More About Email Ferret")
 * @param description - Optional description text below title
 * @param links - Array of internal links to display
 * @param className - Optional custom className for section wrapper
 *
 * @example
 * ```tsx
 * <InternalLinksSection
 *   title="Learn More About Email Ferret"
 *   description="Discover how Email Ferret's AI email management helps you stop spam"
 *   links={[
 *     { href: "/ai-email-management-tool", label: "AI Email Management Tool" },
 *     { href: "/stop-cold-outreach-emails", label: "Stop Cold Outreach Emails" },
 *   ]}
 * />
 * ```
 */
export function InternalLinksSection({
  title = 'Learn More About Email Ferret',
  description,
  links,
  className = '',
}: InternalLinksSectionProps): React.ReactElement {
  return (
    <section className={`mb-16 ${className}`}>
      <div className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl p-8 border border-blue-500/20">
        <h2 className="text-2xl font-bold mb-4 text-center">{title}</h2>
        {description && <p className="text-muted-foreground text-center mb-6">{description}</p>}
        <div className="flex flex-wrap justify-center gap-4">
          {links.map((link) => (
            <Button key={link.href} asChild variant="outline" className="backdrop-blur-sm">
              <Link href={link.href}>{link.label}</Link>
            </Button>
          ))}
        </div>
      </div>
    </section>
  );
}
